import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '@firebird-web/shared-config';
import {
  distinctUntilChanged,
  filter,
  map,
  Observable,
  skip,
  startWith,
} from 'rxjs';
import { ZeusApiRequest } from '../models/zeus.models';

const MAX_REQUESTS = 30;
const MAX_BODY_CHARS = 2000;
// Background polling and app chrome, not the data the user is looking at.
const IGNORED_PATHS =
  /notification|news|blog|blurb|release-notes|user-?settings|permission|auth|token|alerts?-count|heartbeat/i;

// Records the data API calls made for the current page, so Zeus knows exactly
// which region, sites, runs and models are on screen without reading the DOM.
@Injectable({
  providedIn: 'root',
})
export class ZeusApiActivityService {
  private readonly requests = new Map<string, ZeusApiRequest>();

  constructor(router: Router) {
    router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => router.url.split('?')[0]),
        startWith(router.url.split('?')[0]),
        distinctUntilChanged(),
        skip(1)
      )
      .subscribe(() => this.requests.clear());
  }

  public record(request: HttpRequest<unknown>): void {
    if (
      !environment.apiDomain ||
      !request.url.startsWith(environment.apiDomain)
    ) {
      return;
    }
    const path = request.url.slice(environment.apiDomain.length) || '/';
    if (IGNORED_PATHS.test(path)) {
      return;
    }

    const params: Record<string, string | string[]> = {};
    for (const key of request.params.keys()) {
      const values = request.params.getAll(key) ?? [];
      params[key] = values.length === 1 ? values[0] : values;
    }

    const entry: ZeusApiRequest = { method: request.method, path, params };
    if (request.body !== null && request.body !== undefined) {
      const body = JSON.stringify(request.body);
      entry.body =
        body.length > MAX_BODY_CHARS
          ? `${body.slice(0, MAX_BODY_CHARS)}... [truncated]`
          : body;
    }

    // Keyed by endpoint so polling and re-fetches keep only the latest parameters.
    const key = `${request.method} ${path}`;
    this.requests.delete(key);
    this.requests.set(key, entry);
    if (this.requests.size > MAX_REQUESTS) {
      this.requests.delete(this.requests.keys().next().value as string);
    }
  }

  public snapshot(): ZeusApiRequest[] {
    return [...this.requests.values()];
  }
}

@Injectable()
export class ZeusApiActivityInterceptor implements HttpInterceptor {
  constructor(private readonly activity: ZeusApiActivityService) {}

  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    this.activity.record(request);
    return next.handle(request);
  }
}
